import { useEffect, useRef, useState, useCallback } from "react";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";

interface WebSocketMessage {
  content?: any;
}

async function getWebSocketUrl(): Promise<string> {
  const amplifyConfig = localStorage.getItem("amplify_config");
  if (!amplifyConfig) {
    throw new Error("Amplify config not found in localStorage");
  }
  const websocketURL: string = JSON.parse(amplifyConfig).WebsocketURL;
  const token = await AmplifyAPIWrapper.getJwtToken().then((resp) => {
    return resp;
  });
  const url = websocketURL + "?token=" + token;
  return url;
}

const useWebSocket = (reconnectInterval: number = 5000) => {
  const [data, setData] = useState<any | null>(null);
  const [opened, setOpened] = useState<boolean>(false);
  const socket = useRef<WebSocket | null>(null);
  const [shouldReconnect, setShouldReconnect] = useState(true);

  const connect = useCallback(async () => {
    const url = await getWebSocketUrl(); // Get the WebSocket URL with token
    socket.current = new WebSocket(url);

    socket.current.onopen = () => {
      setOpened(true);
    };

    socket.current.onmessage = (event: MessageEvent) => {
      const message: WebSocketMessage = JSON.parse(event.data);
      setData(message);
    };

    socket.current.onerror = (error: Event) => {
      console.error("WebSocket error:", error);
    };

    socket.current.onclose = () => {
      if (shouldReconnect) {
        setTimeout(() => {
          connect();
        }, reconnectInterval);
      }
    };
  }, [reconnectInterval, shouldReconnect]);

  useEffect(() => {
    connect();

    return () => {
      setShouldReconnect(false);
      socket.current?.close();
    };
  }, [connect]);

  const sendMessage = (message: WebSocketMessage) => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(message));
    }
  };
  const clearData = () => {
    setData(null);
  };

  const subscribe = (action: WebSocketMessage) => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(action.content));
    }
  };

  const closeWebSocket = () => {
    setShouldReconnect(false);
    socket.current?.close();
  };

  const openWebSocket = () => {
    setShouldReconnect(true);
    connect();
  };

  return { data, sendMessage, closeWebSocket, openWebSocket, subscribe, clearData, opened };
};
export default useWebSocket;
